import { BlogPost } from "@/data/blogPosts";
import { optimizeBatchContent } from "@/lib/batchContentOptimizer";
import { injectArticleLinks } from "@/lib/articleLinks";
import { BATCH21_SERVICE_LINKS } from "@/lib/batch21ServiceLinks";
import { BATCH22_SERVICE_LINKS } from "@/lib/batch22ServiceLinks";
import { BATCH23SEQ_SERVICE_LINKS } from "@/lib/batch23seqServiceLinks";
import { BATCH27_SERVICE_LINKS } from "@/lib/batch27ServiceLinks";
import { BATCH28_SERVICE_LINKS } from "@/lib/batch28ServiceLinks";
import { BATCH29_SERVICE_LINKS } from "@/lib/batch29ServiceLinks";
import { BATCH51_SERVICE_LINKS } from "@/lib/batch51ServiceLinks";
import type { ServiceLinkEntry } from "@/lib/serviceLinkTypes";

/**
 * Injects contextual internal links into blog post HTML content.
 * - Wraps first occurrence of key phrases from related posts in anchor tags
 * - Adds a "Keep reading" callout with 2 related links at the end
 */
/**
 * Service page link injection map.
 * Blog posts whose slug matches a key get a contextual link to the
 * corresponding service page injected into their first paragraph.
 * This flows link equity from ranking blog posts to commercial pages.
 */
const SERVICE_LINK_MAP: Record<
  string,
  { url: string; anchor: RegExp; label: string }
> = {
  "n8n-workflow-developer-philippines": {
    url: "/n8n-workflow-developer",
    anchor: /n8n workflow developer in the Philippines/i,
    label: "hire an n8n workflow developer",
  },
  "gohighlevel-specialist-philippines": {
    url: "/gohighlevel-revops-architect",
    anchor: /GoHighLevel specialist/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "voice-ai-automation-va-philippines": {
    url: "/voice-ai-vapi-automation",
    anchor: /Voice AI automation/i,
    label: "hire a Voice AI automation specialist",
  },
  "hire-technical-virtual-assistant-philippines": {
    url: "/",
    anchor: /technical virtual assistant/i,
    label: "the RevOps practice behind elite technical hiring",
  },
  "ghl-systems-architect-freelance": {
    url: "/gohighlevel-revops-architect",
    anchor: /GHL systems architect/i,
    label: "hire a GHL systems architect",
  },
  "crm-automation-specialist-philippines": {
    url: "/gohighlevel-revops-architect",
    anchor: /CRM automation specialist/i,
    label: "hire a CRM automation specialist",
  },
  "revops-virtual-assistant-philippines": {
    url: "/gohighlevel-revops-architect",
    anchor: /RevOps virtual assistant/i,
    label: "hire a RevOps specialist",
  },
  "lead-to-cash-automation-freelancer-philippines": {
    url: "/gohighlevel-revops-architect",
    anchor: /lead-to-cash automation/i,
    label: "hire a lead-to-cash automation expert",
  },
  "ai-prompt-engineer-virtual-assistant-philippines": {
    url: "/voice-ai-vapi-automation",
    anchor: /AI prompt engineer/i,
    label: "hire an AI prompt engineer",
  },
  // Batch 6 — HubSpot data-hygiene cluster → /hubspot-revops-architect
  "how-to-clean-up-bloated-hubspot-database": {
    url: "/hubspot-revops-architect",
    anchor: /bloated HubSpot database/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hubspot-portal-audit-specialist-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot portal audit specialist/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hire-expert-reduce-hubspot-contact-tier-costs": {
    url: "/hubspot-revops-architect",
    anchor: /Reducing HubSpot contact-tier costs/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hubspot-crm-database-deduplication-freelancer": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot database deduplication freelancer/i,
    label: "hire a HubSpot RevOps architect",
  },
  "fix-unmapped-custom-properties-hubspot": {
    url: "/hubspot-revops-architect",
    anchor: /unmapped custom properties in HubSpot/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hubspot-data-migration-consultant-remote": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot data migration consultant/i,
    label: "hire a HubSpot RevOps architect",
  },
  "clean-up-messy-hubspot-portal-freelance": {
    url: "/hubspot-revops-architect",
    anchor: /messy HubSpot portal/i,
    label: "hire a HubSpot RevOps architect",
  },
  // Batch 7 — HubSpot architecture cluster → /hubspot-revops-architect
  "outsource-hubspot-data-hygiene-specialist-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot data hygiene/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hubspot-lifecycle-stage-mapping-expert": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot lifecycle-stage mapping expert/i,
    label: "hire a HubSpot RevOps architect",
  },
  "how-to-structure-hubspot-custom-objects": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot custom objects/i,
    label: "hire a HubSpot RevOps architect",
  },
  // Batch 8 — GoHighLevel architecture cluster → /gohighlevel-revops-architect
  "gohighlevel-specialist-philippines-arbitrage": {
    url: "/gohighlevel-revops-architect",
    anchor: /GoHighLevel specialist/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "white-label-gohighlevel-snapshot-developer": {
    url: "/gohighlevel-revops-architect",
    anchor: /white-label GoHighLevel snapshot developer/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "how-to-manage-100-plus-ghl-sub-accounts": {
    url: "/gohighlevel-revops-architect",
    anchor: /100-plus GHL sub-accounts/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "setup-lead-scoring-model-in-gohighlevel": {
    url: "/gohighlevel-revops-architect",
    anchor: /lead scoring model in GoHighLevel/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "behavioral-trigger-mapping-expert-freelance": {
    url: "/zapier-make-automation-expert",
    anchor: /behavioral trigger mapping expert/i,
    label: "hire an automation architect",
  },
  "automated-asset-collection-workflow-for-agency-clients": {
    url: "/zapier-make-automation-expert",
    anchor: /automated asset collection workflow/i,
    label: "hire an automation architect",
  },
  // Batch 8 — Meta Ads tracking → /meta-ads-performance-specialist
  "meta-conversion-api-tracking-setup-multiple-locations-guide": {
    url: "/meta-ads-performance-specialist",
    anchor: /Meta conversion API tracking setup/i,
    label: "hire a Meta Ads performance specialist",
  },
  // Batch 9 — service-topic posts.
  "lead-to-cash-automation-specialist-freelance": {
    url: "/gohighlevel-revops-architect",
    anchor: /lead-to-cash automation specialist/i,
    label: "hire a lead-to-cash automation expert",
  },
  "revops-virtual-assistant-philippines-expert": {
    url: "/gohighlevel-revops-architect",
    anchor: /RevOps virtual assistant in the Philippines/i,
    label: "hire a RevOps specialist",
  },
  "manage-100-plus-sub-accounts-gohighlevel": {
    url: "/gohighlevel-revops-architect",
    anchor: /manage 100 plus sub accounts in GHL/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "meta-conversion-api-tracking-setup-multiple-locations": {
    url: "/meta-ads-performance-specialist",
    anchor: /Meta conversion API tracking setup for multiple locations/i,
    label: "hire a Meta Ads performance specialist",
  },
  "reduce-agency-admin-work-80-percent": {
    url: "/n8n-workflow-developer",
    anchor: /n8n workflow integrations/i,
    label: "hire an n8n workflow developer",
  },
  "how-to-automate-client-onboarding-marketing-agency": {
    url: "/n8n-workflow-developer",
    anchor: /n8n workflow integration/i,
    label: "hire an n8n workflow developer",
  },
  "reduce-high-ticket-agency-churn-with-automation": {
    url: "/n8n-workflow-developer",
    anchor: /n8n workflow integrations/i,
    label: "hire an n8n workflow developer",
  },
  // Batch 10 — service-topic posts.
  "meta-conversion-api-tracking-setup-for-multiple-locations": {
    url: "/meta-ads-performance-specialist",
    anchor: /Meta conversion API tracking setup for multiple locations/i,
    label: "hire a Meta Ads performance specialist",
  },
  "how-to-manage-100-plus-sub-accounts-in-ghl": {
    url: "/gohighlevel-revops-architect",
    anchor: /manage 100 plus sub accounts in GHL/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "behavioral-trigger-mapping-expert-timing": {
    url: "/zapier-make-automation-expert",
    anchor: /behavioral trigger mapping expert/i,
    label: "hire an automation architect",
  },
  "automated-lead-response-workflow-under-30-seconds": {
    url: "/n8n-workflow-developer",
    anchor: /automated lead response workflow/i,
    label: "hire an n8n workflow developer",
  },
  "sales-psychology-automation-freelancer-intent": {
    url: "/zapier-make-automation-expert",
    anchor: /sales psychology automation freelancer/i,
    label: "hire an automation architect",
  },
  "how-to-reduce-agency-admin-work-by-80-percent": {
    url: "/n8n-workflow-developer",
    anchor: /n8n workflow integration/i,
    label: "hire an n8n workflow developer",
  },
  "lead-to-cash-automation-specialist-roi": {
    url: "/gohighlevel-revops-architect",
    anchor: /lead to cash automation specialist/i,
    label: "hire a lead-to-cash automation expert",
  },
  "how-to-reduce-agency-churn-with-automated-onboarding": {
    url: "/gohighlevel-revops-architect",
    anchor: /reduce agency churn with automated onboarding/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "automated-asset-collection-workflow-for-agencies": {
    url: "/zapier-make-automation-expert",
    anchor: /automated asset collection workflow for agencies/i,
    label: "hire an automation architect",
  },
  // Batch 11 — HubSpot architecture cluster → /hubspot-revops-architect
  "hire-hubspot-marketing-hub-specialist-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot Marketing Hub Specialist/i,
    label: "hire a HubSpot RevOps architect",
  },
  "how-to-build-advanced-hubspot-workflows": {
    url: "/hubspot-revops-architect",
    anchor: /advanced HubSpot workflows/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hubspot-lead-scoring-model-setup-freelancer": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot lead scoring model/i,
    label: "hire a HubSpot RevOps architect",
  },
  "automated-lead-rotation-logic-hubspot-architect": {
    url: "/hubspot-revops-architect",
    anchor: /automated lead rotation logic/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hire-professional-to-set-up-hubspot-sequences": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot sequences/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hubspot-multi-branch-workflow-architect-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /multi-branch workflow architect/i,
    label: "hire a HubSpot RevOps architect",
  },
  "behavioral-trigger-mapping-hubspot-freelance": {
    url: "/hubspot-revops-architect",
    anchor: /behavioral trigger mapping in HubSpot/i,
    label: "hire a HubSpot RevOps architect",
  },
  "setup-hubspot-smart-content-personalized-funnels": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot smart content/i,
    label: "hire a HubSpot RevOps architect",
  },
  "outsource-hubspot-newsletter-automation-setup": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot newsletter automation/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hubspot-multi-touch-attribution-reporting-expert": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot multi-touch attribution reporting expert/i,
    label: "hire a HubSpot RevOps architect",
  },
  "connect-hubspot-to-n8n-workflow-developer": {
    url: "/n8n-workflow-developer",
    anchor: /connect HubSpot to an n8n workflow developer/i,
    label: "hire an n8n workflow developer",
  },
  "alternative-to-traditional-bpo-call-centers-in-the-philippines": {
    url: "/voice-ai-vapi-automation",
    anchor: /agentic voice AI/i,
    label: "hire a Voice AI automation specialist",
  },
  // Batch 12 — service-topic posts.
  "hubspot-api-integration-specialist-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot API integration specialist/i,
    label: "hire a HubSpot RevOps architect",
  },
  "how-to-securely-connect-vapi-voice-ai-hubspot-n8n": {
    url: "/voice-ai-vapi-automation",
    anchor: /securely connect a Vapi Voice AI/i,
    label: "hire a Voice AI automation specialist",
  },
  "crm-automation-virtual-assistant-philippines": {
    url: "/gohighlevel-revops-architect",
    anchor: /CRM automation virtual assistant/i,
    label: "hire a CRM automation architect",
  },
  "fractional-revenue-operations-revops-architect-remote": {
    url: "/gohighlevel-revops-architect",
    anchor: /fractional revenue operations revops architect/i,
    label: "hire a fractional RevOps architect",
  },
  "remote-hubspot-certified-workflow-manager": {
    url: "/hubspot-revops-architect",
    anchor: /remote HubSpot certified workflow manager/i,
    label: "hire a HubSpot RevOps architect",
  },
  "zapier-and-n8n-integration-specialist-remote": {
    url: "/n8n-workflow-developer",
    anchor: /Zapier and n8n integration specialist/i,
    label: "hire an n8n workflow developer",
  },
  // Batch 13 — Technical Operations / Security / Compliance cluster.
  "virtual-assistant-set-up-sales-funnels": {
    url: "/gohighlevel-revops-architect",
    anchor: /virtual assistant to set up sales funnels/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "expert-fix-broken-crm-webhooks": {
    url: "/gohighlevel-revops-architect",
    anchor: /expert to fix broken CRM webhooks/i,
    label: "hire a CRM automation architect",
  },
  "outsource-website-maintenance-plugin-management": {
    url: "/gohighlevel-revops-architect",
    anchor: /outsource website maintenance and plugin management/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "lead-scoring-model-setup-virtual-assistant": {
    url: "/gohighlevel-revops-architect",
    anchor: /lead scoring model setup virtual assistant/i,
    label: "hire a lead-to-cash automation expert",
  },
  "b2b-email-deliverability-spf-tracking-specialist": {
    url: "/zapier-make-automation-expert",
    anchor: /B2B email deliverability and SPF tracking specialist/i,
    label: "hire an automation architect",
  },
  "securely-share-passwords-overseas-remote-contractors": {
    url: "/hubspot-revops-architect",
    anchor: /securely share passwords with overseas remote contractors/i,
    label: "hire a HubSpot RevOps architect",
  },
  "gdpr-hipaa-compliant-virtual-assistant-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /GDPR and HIPAA compliant virtual assistant/i,
    label: "hire a HubSpot RevOps architect",
  },
  // Batch 14 — RevOps / AI Automation / Voice AI / Hiring-Decision cluster.
  "revenue-operations-virtual-assistant-growth": {
    url: "/gohighlevel-revops-architect",
    anchor: /revenue operations virtual assistant/i,
    label: "hire a RevOps specialist",
  },
  "revops-architect-freelance-philippines-scale": {
    url: "/gohighlevel-revops-architect",
    anchor: /RevOps architect freelance in the Philippines/i,
    label: "hire a RevOps architect",
  },
  "lead-scoring-model-setup-freelancer": {
    url: "/gohighlevel-revops-architect",
    anchor: /lead scoring model setup freelancer/i,
    label: "hire a lead-to-cash automation expert",
  },
  "ai-objection-handling-workflow-setup-close": {
    url: "/n8n-workflow-developer",
    anchor: /AI objection handling workflow setup/i,
    label: "hire an n8n workflow developer",
  },
  "llm-integrated-automation-specialist-data": {
    url: "/n8n-workflow-developer",
    anchor: /LLM integrated automation specialist/i,
    label: "hire an n8n workflow developer",
  },
  "agentic-ai-workflow-developer-freelancer-future": {
    url: "/n8n-workflow-developer",
    anchor: /agentic AI workflow developer freelancer/i,
    label: "hire an n8n workflow developer",
  },
  "behavioral-trigger-mapping-expert": {
    url: "/zapier-make-automation-expert",
    anchor: /behavioral trigger mapping expert/i,
    label: "hire an automation architect",
  },
  "twilio-voice-ai-automation-developer-scale": {
    url: "/voice-ai-vapi-automation",
    anchor: /Twilio voice AI automation developer/i,
    label: "hire a Voice AI automation specialist",
  },
  "vapi-voice-ai-integration-specialist-inbound": {
    url: "/voice-ai-vapi-automation",
    anchor: /Vapi voice AI integration specialist/i,
    label: "hire a Voice AI automation specialist",
  },
  // Batch 15 — RevOps / CRM Reliability / HubSpot Enterprise / GHL Architecture /
  // AI Automation cluster.
  "sales-psychology-automation-freelancer": {
    url: "/zapier-make-automation-expert",
    anchor: /sales psychology automation freelancer/i,
    label: "hire an automation architect",
  },
  "crm-error-handling-fallbacks-developer-safety": {
    url: "/gohighlevel-revops-architect",
    anchor: /CRM error handling and fallbacks developer/i,
    label: "hire a CRM automation architect",
  },
  "lead-to-cash-automation-specialist": {
    url: "/gohighlevel-revops-architect",
    anchor: /lead to cash automation specialist/i,
    label: "hire a lead-to-cash automation expert",
  },
  "enterprise-hubspot-systems-architect-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /enterprise HubSpot systems architect in the Philippines/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hubspot-marketing-automation-developer-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot marketing automation developer in the Philippines/i,
    label: "hire a HubSpot RevOps architect",
  },
  "hire-dedicated-hubspot-manager-upwork-alternative": {
    url: "/hubspot-revops-architect",
    anchor: /dedicated HubSpot manager/i,
    label: "hire a HubSpot RevOps architect",
  },
  "ghl-custom-values-fields-specialist": {
    url: "/gohighlevel-revops-architect",
    anchor: /GHL custom values and fields specialist/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "hire-gohighlevel-architect-philippines-roi": {
    url: "/gohighlevel-revops-architect",
    anchor: /GoHighLevel architect in the Philippines/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "ai-objection-handling-workflow-setup": {
    url: "/n8n-workflow-developer",
    anchor: /AI objection handling workflow setup/i,
    label: "hire an n8n workflow developer",
  },
  "revenue-operations-virtual-assistant": {
    url: "/gohighlevel-revops-architect",
    anchor: /Revenue Operations virtual assistant/i,
    label: "hire a RevOps specialist",
  },
  "revops-architect-freelance-philippines": {
    url: "/gohighlevel-revops-architect",
    anchor: /RevOps architect freelance in the Philippines/i,
    label: "hire a RevOps architect",
  },
  // Batch 16 — Security / CRM-data-access cluster → /n8n-workflow-developer.
  "handling-remote-assistant-access-sensitive-client-crm-data": {
    url: "/n8n-workflow-developer",
    anchor: /n8n workflow integrations/i,
    label: "hire an n8n workflow developer",
  },
  // Batch 17 — service-topic posts.
  "llm-integrated-automation-specialist": {
    url: "/n8n-workflow-developer",
    anchor: /LLM integrated automation specialist/i,
    label: "hire an n8n workflow developer",
  },
  "agentic-ai-workflow-developer-freelancer": {
    url: "/n8n-workflow-developer",
    anchor: /agentic AI workflow developer freelancer/i,
    label: "hire an n8n workflow developer",
  },
  "gohighlevel-api-integration-specialist-custom": {
    url: "/gohighlevel-revops-architect",
    anchor: /GoHighLevel API integration specialist/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "ghl-multi-account-management-expert-growth": {
    url: "/gohighlevel-revops-architect",
    anchor: /GHL multi account management expert/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "outsource-hubspot-support-specialist-remote": {
    url: "/hubspot-revops-architect",
    anchor: /remote HubSpot support specialist/i,
    label: "hire a HubSpot RevOps architect",
  },
  "best-hubspot-consultants-in-the-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /best HubSpot consultants in the Philippines/i,
    label: "hire a HubSpot RevOps architect",
  },
  "fractional-hubspot-revops-architect": {
    url: "/hubspot-revops-architect",
    anchor: /fractional HubSpot RevOps architect/i,
    label: "hire a HubSpot RevOps architect",
  },
  "twilio-voice-ai-automation-developer": {
    url: "/voice-ai-vapi-automation",
    anchor: /Twilio voice AI automation developer/i,
    label: "hire a Voice AI automation specialist",
  },
  "vapi-voice-ai-integration-specialist": {
    url: "/voice-ai-vapi-automation",
    anchor: /Vapi voice AI integration specialist/i,
    label: "hire a Voice AI automation specialist",
  },
  // Batch 18 — service-topic posts.
  "white-label-gohighlevel-setup-freelancer": {
    url: "/gohighlevel-revops-architect",
    anchor: /white label GoHighLevel setup freelancer/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "crm-error-handling-and-fallbacks-developer": {
    url: "/gohighlevel-revops-architect",
    anchor: /CRM error handling and fallbacks developer/i,
    label: "hire a CRM automation architect",
  },
  "ghl-custom-values-and-fields-specialist": {
    url: "/gohighlevel-revops-architect",
    anchor: /GHL custom values and fields specialist/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "custom-api-integration-virtual-assistant": {
    url: "/n8n-workflow-developer",
    anchor: /Custom API integration virtual assistant/i,
    label: "hire an n8n workflow developer",
  },
  "connect-gohighlevel-to-n8n-specialist": {
    url: "/n8n-workflow-developer",
    anchor: /connect GoHighLevel to n8n/i,
    label: "hire an n8n workflow developer",
  },
  "n8n-webhook-engineering-consultant": {
    url: "/n8n-workflow-developer",
    anchor: /n8n webhook engineering consultant/i,
    label: "hire an n8n workflow developer",
  },
  "n8n-workflow-developer-freelance": {
    url: "/n8n-workflow-developer",
    anchor: /freelance n8n workflow developer/i,
    label: "hire an n8n workflow developer",
  },
  "anatomy-of-a-revenue-engine": {
    url: "/gohighlevel-revops-architect",
    anchor: /high-converting revenue engine/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "ghl-automation-expert-philippines": {
    url: "/gohighlevel-revops-architect",
    anchor: /GHL automation expert in the Philippines/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  // Batch 19 — HubSpot operations manager → /hubspot-revops-architect.
  "remote-hubspot-operations-manager-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /remote HubSpot operations manager in the Philippines/i,
    label: "hire a HubSpot RevOps architect",
  },
  // Batch 20 — platform-specific service links.
  "saas-onboarding-virtual-assistant": {
    url: "/n8n-workflow-developer",
    anchor: /zero-touch onboarding flows/i,
    label: "hire an n8n workflow developer",
  },
  "hubspot-certified-administrator-freelancer-philippines": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot certified administrator freelancer in the Philippines/i,
    label: "hire a HubSpot RevOps architect",
  },
  "zapier-workflow-virtual-assistant": {
    url: "/n8n-workflow-developer",
    anchor: /systems architect/i,
    label: "hire an n8n workflow developer",
  },
  "n8n-automation-virtual-assistant": {
    url: "/n8n-workflow-developer",
    anchor: /n8n automation virtual assistant/i,
    label: "hire an n8n workflow developer",
  },
  "hubspot-certified-virtual-assistant": {
    url: "/hubspot-revops-architect",
    anchor: /HubSpot certified virtual assistant/i,
    label: "hire a HubSpot RevOps architect",
  },
  "gohighlevel-specialist-virtual-assistant": {
    url: "/gohighlevel-revops-architect",
    anchor: /GoHighLevel specialist virtual assistant/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "crm-database-virtual-assistant": {
    url: "/gohighlevel-revops-architect",
    anchor: /CRM database virtual assistant/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "b2b-sales-funnel-virtual-assistant": {
    url: "/gohighlevel-revops-architect",
    anchor: /B2B sales funnel virtual assistant/i,
    label: "hire a GoHighLevel RevOps architect",
  },
  "technical-virtual-assistant-hubspot-expert": {
    url: "/hubspot-revops-architect",
    anchor: /technical virtual assistant HubSpot expert/i,
    label: "hire a HubSpot RevOps architect",
  },
  // Batch 21 — platform-specific service links (merged from a focused module to
  // keep this file within edit limits).
  ...BATCH21_SERVICE_LINKS,
  // Batch 22 — platform-specific service links (n8n/automation + AI/voice-AI).
  ...BATCH22_SERVICE_LINKS,
  // Sequential Batch 23 — platform-specific service links (voice-AI + n8n + GHL).
  ...BATCH23SEQ_SERVICE_LINKS,
  // Batch 27 — HubSpot architecture / API / webhook / reporting cluster →
  // /hubspot-revops-architect. (The remote-readiness post is a hiring topic →
  // homepage link via HIRING_TOPIC_SLUGS, not a service link.)
  ...BATCH27_SERVICE_LINKS,
  // Batch 28 — GHL architecture / n8n / HubSpot pipeline analytics cluster.
  // 9 platform topics → service-page links. (3 hiring/role topics get a
  // homepage link via HIRING_TOPIC_SLUGS, not a service link.)
  ...BATCH28_SERVICE_LINKS,
  // Batch 29 — HubSpot Reporting / Custom Property / Pipeline Velocity /
  // Forecasting cluster (4 platform topics) → /hubspot-revops-architect.
  // (8 legal/compliance/management hiring topics get a homepage link via
  // ALL_HIRING_SLUGS, not a service link.)
  ...BATCH29_SERVICE_LINKS,
};

/**
 * Checks if a position in HTML content is inside an existing anchor tag.
 */
function isInsideAnchor(html: string, pos: number): boolean {
  const before = html.substring(0, pos);
  const lastOpen = before.lastIndexOf("<a ");
  const lastClose = before.lastIndexOf("</a>");
  return lastOpen > lastClose;
}

/**
 * Injects a service page link into the first paragraph of blog posts
 * that have a matching entry in SERVICE_LINK_MAP.
 * Link appears within the first ~100 words for maximum link equity flow.
 */
function injectServiceLink(content: string, slug: string): string {
  const match = SERVICE_LINK_MAP[slug];
  if (!match) return content;

  // Find the anchor text in the content (case-insensitive)
  const lowerContent = content.toLowerCase();
  const anchorPattern = match.anchor.source.toLowerCase();
  const idx = lowerContent.indexOf(anchorPattern);
  if (idx === -1) return content;

  // Check it's not already inside an anchor tag
  if (isInsideAnchor(content, idx)) return content;

  const actualText = content.substring(idx, idx + anchorPattern.length);
  const replacement = `<a href="${match.url}" class="text-primary hover:text-primary/80 underline decoration-primary/40 hover:decoration-primary transition-all font-medium" title="${match.label}">${actualText}</a>`;

  return (
    content.substring(0, idx) +
    replacement +
    content.substring(idx + anchorPattern.length)
  );
}

export function injectContextualLinks(
  content: string,
  currentPost: BlogPost,
  allPosts: BlogPost[],
): string {
  // Apply Batch in-body optimizations (answer-first leads, info-gain
  // assets, /#contact → /book CTA migration, hiring-topic homepage links).
  const optimized = optimizeBatchContent(content, currentPost.slug);
  // Inject service page link (highest priority - commercial intent).
  const withService = injectServiceLink(optimized, currentPost.slug);
  // Inject article-to-article links + "Keep reading" callout (delegated to a
  // focused module). The callout is the reliable mechanism: it always
  // surfaces up to 3 related posts as clickable /blog/<slug> links, even when
  // in-body phrase matching finds nothing.
  return injectArticleLinks(withService, currentPost, allPosts);
}
